import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import supabase from '../../supabase'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '../../context/AuthContext'

export default function LogIn() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [loading, setLoading] = useState(false)

const handleSignIn = async (e) => {
  e.preventDefault();
  setLoading(true);
  setErrorMsg('');

  const { error, data } = await supabase.auth.signInWithPassword({ email, password });

  if (error) {
    setErrorMsg(error.message);
    setLoading(false);
  } else {
    // 1. Check for the tracked URL
    const returnTo = sessionStorage.getItem("last_valid_url");

    if (returnTo) {
      // 2. Clear it so it doesn't stay in memory
      sessionStorage.removeItem("last_valid_url");
      
      // 3. Redirect back to where they were
      navigate(returnTo);
    } else {
      // 4. Fallback to dashboard or home
      navigate('/'); 
    }
  }
};

  const handleGoogleSignIn = async () => {
    const { error } = await supabase.auth.signInWithOAuth({ provider: 'google' })
    if (error) setErrorMsg('Google sign-in failed: ' + error.message)
  }

  return (
    <div className='min-h-screen w-full bg-[#f9f9f9] flex items-center justify-center px-4 py-20'>
      <Helmet>
        <title>Login | Sitemanifest</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Main Elegant Card */}
      <div className='w-full max-w-[450px] mt-[30px] lg:mt-[50px] bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 p-8 lg:p-12 transition-all'>
        
        {/* Header */}
        <div className='flex flex-col items-center mb-10'>
          <h1 className='font-staat text-[#1C6065] text-3xl tracking-wider mb-2'>LOG IN</h1>
          <div className='flex items-center gap-2'>
            <span className='text-gray-400 text-sm font-poppins'>New to the registry?</span>
            <button 
              onClick={() => navigate('/signup')}
              className='text-[#1E7D6B] text-sm font-semibold hover:underline decoration-2 underline-offset-4 transition-all'
            >
              Create Account
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSignIn} className='space-y-5'>
          <div className='space-y-1.5'>
            <label className='text-[10px] uppercase tracking-[2px] font-bold text-[#1C6065] opacity-60 ml-1'>Email Address</label>
            <input
              type="email"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-gray-50 border border-gray-200 placeholder-gray-300 text-[#1C6065] text-sm h-[52px] rounded-xl px-4 focus:bg-white focus:border-[#1E7D6B] focus:ring-1 focus:ring-[#1E7D6B] outline-none transition-all"
            />
          </div>

          <div className='space-y-1.5'>
            <div className='flex justify-between items-center px-1'>
              <label className='text-[10px] uppercase tracking-[2px] font-bold text-[#1C6065] opacity-60'>Password</label>
              <button onClick={() => navigate('/forgot-password')} type="button" className='text-[10px] uppercase tracking-[1px] font-bold text-[#7CD998] hover:opacity-80'>Forgot?</button>
            </div>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-gray-50 border border-gray-200 placeholder-gray-300 text-[#1C6065] text-sm h-[52px] rounded-xl px-4 focus:bg-white focus:border-[#1E7D6B] focus:ring-1 focus:ring-[#1E7D6B] outline-none transition-all"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1C6065] hover:bg-[#164d51] h-[54px] rounded-xl font-poppins text-sm font-bold tracking-[1px] text-white shadow-lg shadow-[#1c606520] transition-all transform active:scale-[0.98] mt-4"
          >
            {loading ? 'AUTHENTICATING...' : 'ACCESS ACCOUNT'}
          </button>
        </form>

        {/* Divider */}
        <div className='flex items-center my-8 gap-4'>
          <div className='h-[1px] flex-1 bg-gray-100'></div>
          <span className='text-[10px] font-bold text-gray-300 tracking-widest uppercase'>OR</span>
          <div className='h-[1px] flex-1 bg-gray-100'></div>
        </div>

        {/* Google Button */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          className="w-full flex items-center justify-center gap-3 bg-white border border-gray-200 h-[54px] rounded-xl font-poppins text-sm font-semibold text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all active:scale-[0.98]"
        >
          <svg className="w-5 h-5" viewBox="0 0 533.5 544.3">
            <path fill="#4285f4" d="M533.5 278.4c0-17.8-1.5-35-4.3-51.4H272v97.3h147.6c-6.3 34.5-25.2 63.8-53.7 83.2v68h86.7c50.8-46.8 80-115.9 80-197.1z" />
            <path fill="#34a853" d="M272 544.3c72.6 0 133.5-24 178-65.3l-86.7-68c-24 16.1-54.7 25.6-91.3 25.6-70 0-129.2-47.2-150.4-110.6H32.5v69.6c44.7 89.4 137.6 149.7 239.5 149.7z" />
            <path fill="#fbbc04" d="M121.6 325.9c-10.3-30.1-10.3-62.8 0-92.9V163.4h-89.1c-39.1 77.8-39.1 168.6 0 246.4l89.1-69.9z" />
            <path fill="#ea4335" d="M272 107.4c39.4 0 75 13.6 103 40.5l77-77C405.3 25.1 345.2 0 272 0 170.1 0 77.2 60.3 32.5 149.7l89.1 69.6C142.8 154.6 202 107.4 272 107.4z" />
          </svg>
          Continue with Google
        </button>

        {errorMsg && (
          <p className="mt-6 text-red-500 text-xs text-center font-medium bg-red-50 py-2 rounded-lg">{errorMsg}</p>
        )}
      </div>
    </div>
  )
}